// ============================================================
// MetricCard — Brand Guide v2.
//
// Surface 1 on the void · accent border when tone ≠ default.
// Glow = meaning: only rendered when `live={true}`. Static
// historical metrics stay calm so the command center never
// lies about what's breathing.
// ============================================================

import type { ReactNode } from 'react'
import { SHELL_TOKENS, text } from '../shell/styles'

export type MetricTone = 'default' | 'warning' | 'critical' | 'success' | 'info'

interface MetricCardProps {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: MetricTone
  /** Only pass live={true} when the underlying value is actually breathing. */
  live?: boolean
}

function accentFor(tone: MetricTone): string {
  switch (tone) {
    case 'critical': return SHELL_TOKENS.red
    case 'warning':  return SHELL_TOKENS.gold
    case 'success':  return SHELL_TOKENS.green
    case 'info':     return SHELL_TOKENS.cyan
    default:         return SHELL_TOKENS.orange // Identity accent
  }
}

export default function MetricCard({
  label,
  value,
  sub,
  tone = 'default',
  live = false,
}: MetricCardProps) {
  const accent = accentFor(tone)
  const accented = tone !== 'default'

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '16px 18px',
        borderRadius: 12,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${accented ? accent + '26' : SHELL_TOKENS.border}`,
        boxShadow: live ? `0 0 12px ${accent}14` : 'none',
        animation: live ? 'sc-breathe 4s ease infinite' : 'none',
      }}
    >
      {live && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: `linear-gradient(90deg, ${accent}66 0%, ${accent}22 40%, transparent 80%)`,
          }}
        />
      )}
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: SHELL_TOKENS.inkFaint,
          fontFamily: 'var(--sc-font-mono, var(--font-mono))',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          ...text.metric,
          color: accented || live ? accent : SHELL_TOKENS.ink,
          marginBottom: sub ? 6 : 0,
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 11,
            color: SHELL_TOKENS.inkFaint,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            letterSpacing: '0.06em',
          }}
        >
          {sub}
        </div>
      )}
    </div>
  )
}
